import React, { useState, useEffect, useRef } from 'react';
import { Investor } from '../../types/User';
import './InvestorAutocomplete.css';

interface InvestorAutocompleteProps {
  investors: Investor[];
  value: string;
  onChange: (investorId: string) => void;
  required?: boolean;
}

const InvestorAutocomplete: React.FC<InvestorAutocompleteProps> = ({
  investors,
  value,
  onChange,
  required = false
}) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredInvestors, setFilteredInvestors] = useState<Investor[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Actualizar texto de búsqueda cuando cambia el valor seleccionado
  useEffect(() => {
    if (value) {
      const selectedInvestor = investors.find(inv => inv.id === value);
      if (selectedInvestor) {
        setSearchText(selectedInvestor.nombre);
      }
    } else {
      setSearchText('');
    }
  }, [value, investors]);

  // Filtrar inversores mientras se escribe
  useEffect(() => {
    if (searchText) {
      const filtered = investors.filter(investor =>
        investor.nombre.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredInvestors(filtered);
    } else {
      setFilteredInvestors(investors);
    }
  }, [searchText, investors]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);

    // Solo mostrar dropdown si hay al menos 3 caracteres
    setShowDropdown(text.length >= 3);

    // Si se borra el texto, limpiar la selección
    if (!text) {
      onChange('');
    }
  };

  const handleInvestorSelect = (investor: Investor) => {
    setSearchText(investor.nombre);
    onChange(investor.id!);
    setShowDropdown(false);
  };

  const handleFocus = () => {
    // Solo mostrar dropdown al hacer foco si ya hay al menos 3 caracteres
    if (searchText.length >= 3) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="investor-autocomplete" ref={containerRef}>
      <input
        type="text"
        value={searchText}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder="Buscar inversor (min. 3 letras)..."
        required={required}
        autoComplete="off"
      />

      {showDropdown && searchText.length >= 3 && filteredInvestors.length > 0 && (
        <div className="investor-dropdown">
          {filteredInvestors.map((investor) => (
            <div
              key={investor.id}
              className={`investor-option ${investor.id === value ? 'selected' : ''}`}
              onClick={() => handleInvestorSelect(investor)}
            >
              {investor.nombre}
            </div>
          ))}
        </div>
      )}

      {showDropdown && searchText.length >= 3 && filteredInvestors.length === 0 && (
        <div className="investor-dropdown">
          <div className="investor-option no-results">
            No se encontraron inversores
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorAutocomplete;
